// A thin client for the staff API. Cookies carry the session; the custom header is the
// cross-site request forgery guard the API insists on for every write.

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:8080'

export class ApiError extends Error {
  status: number
  code: string
  detail: string
  extra: Record<string, unknown>
  constructor(status: number, code: string, detail: string, extra: Record<string, unknown> = {}) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
    this.extra = extra
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export async function api<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Requested-With': 'tabula' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  let res: Response
  try {
    res = await fetch(`${API_URL}${path}`, { method, headers, credentials: 'include', body: body === undefined ? undefined : JSON.stringify(body) })
  } catch {
    throw new ApiError(0, 'network', 'Could not reach the server. Check your connection and try again.')
  }
  if (res.status === 204) return undefined as T
  const text = await res.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = null
    }
  }
  if (!res.ok) {
    const p = (data ?? {}) as { code?: string; detail?: string; message?: string; title?: string }
    const extra = { ...(data as Record<string, unknown>) }
    throw new ApiError(res.status, p.code ?? (res.status === 401 ? 'unauthenticated' : 'error'), p.detail ?? p.message ?? p.title ?? `Request failed (${res.status})`, extra)
  }
  return data as T
}

export const get = <T>(path: string) => api<T>('GET', path)
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body ?? {})
export const put = <T>(path: string, body: unknown) => api<T>('PUT', path, body)
export const patch = <T>(path: string, body: unknown) => api<T>('PATCH', path, body)
export const del = <T>(path: string) => api<T>('DELETE', path)

/** Where the browser goes to start a provider sign-in; the API redirects back to the panel. */
export function oauthStartUrl(provider: string, opts: { returnTo?: string; remember?: boolean; invite?: string } = {}): string {
  const q = new URLSearchParams()
  if (opts.returnTo) q.set('returnTo', opts.returnTo)
  if (opts.remember) q.set('remember', '1')
  if (opts.invite) q.set('invite', opts.invite)
  const qs = q.toString()
  return `${API_URL}/v1/auth/oauth/${provider}/start${qs ? `?${qs}` : ''}`
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.detail
  if (err instanceof Error) return err.message
  return 'Something went wrong.'
}
